<template>
    <!-- Renderiza como router-link si se proporciona la prop 'to' -->
    <router-link v-if="to" :to="to" :class="computedClasses">
        <i v-if="icon && !loading" :class="[icon, 'tw:text-xs']"></i>
        <i v-if="loading" class="fas fa-spinner fa-spin tw:text-xs"></i>
        <span>
            <slot>{{ label }}</slot>
        </span>
    </router-link>

    <!-- Renderiza como un botón normal si no tiene 'to' -->
    <button v-else :type="type" :disabled="disabled || loading" @click="$emit('click', $event)"
        :class="computedClasses">
        <i v-if="icon && !loading" :class="[icon, 'tw:text-xs']"></i>
        <i v-if="loading" class="fas fa-spinner fa-spin tw:text-xs"></i>
        <span>
            <slot>{{ label }}</slot>
        </span>
    </button>
</template>

<script>
export default {
    name: 'BaseButton',
    props: {
        // Si se pasa 'to', actúa como router-link
        to: {
            type: [String, Object],
            default: null
        },
        // Tipo de botón HTML: 'button', 'submit', 'reset'
        type: {
            type: String,
            default: 'button'
        },
        // Variante de color visual
        variant: {
            type: String,
            default: 'primary', // primary, secondary, success, danger, outline, etc.
            validator: (value) => ['primary', 'secondary', 'success', 'danger', 'dark', 'outline', 'outline-secondary', 'outline-primary', 'outline-success', 'outline-danger', 'outline-dark', 'ghost'].includes(value)
        },
        // Tamaño del botón
        size: {
            type: String,
            default: 'md', // sm, md, lg
            validator: (value) => ['sm', 'md', 'lg'].includes(value)
        },
        // Icono en FontAwesome (ej. 'fas fa-plus')
        icon: {
            type: String,
            default: ''
        },
        // Texto alternativo si no usas slots
        label: {
            type: String,
            default: ''
        },
        // Estado de carga (muestra spinner)
        loading: {
            type: Boolean,
            default: false
        },
        // Estado deshabilitado
        disabled: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        computedClasses() {
            const base = 'tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:font-semibold tw:transition-all tw:no-underline tw:cursor-pointer tw:border-0 tw:disabled:opacity-60 tw:disabled:cursor-not-allowed';

            // Tamaños
            const sizes = {
                sm: 'tw:px-3 tw:py-1.5 tw:text-xs tw:rounded-lg',
                md: 'tw:px-4 tw:py-2.5 tw:text-sm tw:rounded-xl',
                lg: 'tw:px-5 tw:py-3 tw:text-base tw:rounded-2xl'
            };

            // Variantes de color
            const variants = {
                primary: 'tw:bg-brand-primary tw:hover:bg-brand-primary/80 tw:text-white tw:shadow-md tw:shadow-brand-primary/20',
                secondary: 'tw:bg-slate-800 tw:hover:bg-slate-900 tw:text-white tw:shadow-md tw:shadow-slate-800/20',
                success: 'tw:bg-emerald-600 tw:hover:bg-emerald-700 tw:text-white tw:shadow-md tw:shadow-emerald-600/20',
                danger: 'tw:bg-rose-600 tw:hover:bg-rose-700 tw:text-white tw:shadow-md tw:shadow-rose-600/20',
                dark: 'tw:bg-slate-900 tw:hover:bg-slate-800 tw:text-white',
                outline: 'tw:bg-white tw:hover:bg-slate-50 tw:text-slate-700 tw:border tw:border-slate-200 tw:shadow-sm',
                ghost: 'tw:bg-transparent tw:hover:bg-slate-100 tw:text-slate-600',
                'outline-secondary': 'tw:bg-white tw:hover:bg-slate-100 tw:text-slate-700 tw:border tw:border-slate-300 tw:shadow-sm',
                'outline-primary': 'tw:bg-brand-primary/10 tw:hover:bg-brand-primary/20 tw:text-brand-primary tw:border tw:border-brand-primary/30',
                'outline-success': 'tw:bg-emerald-50 tw:hover:bg-emerald-100 tw:text-emerald-700 tw:border tw:border-emerald-200',
                'outline-danger': 'tw:bg-rose-50 tw:hover:bg-rose-100 tw:text-rose-700 tw:border tw:border-rose-200',
                'outline-dark': 'tw:bg-slate-100 tw:hover:bg-slate-200 tw:text-slate-700 tw:border tw:border-slate-300',
            };

            return [
                base,
                sizes[this.size] || sizes.md,
                variants[this.variant] || variants.primary
            ];
        }
    }
}
</script>